import { Alert, IconButton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { useEffect, useState } from "react"
import { useRoleAssumption } from "../../hooks/useRoleAssumption"

/**
 * Persistent banner shown across all routes while a role-assumption
 * session is active. Sits at the top of AppLayout so the operator never
 * forgets which role/org their actions are scoped to.
 *
 * Visual states (theme-driven via MUI Alert severity):
 *   - Normal: warning (amber) filled Alert
 *   - Last 60 seconds: error (red) filled Alert to escalate before auto-expiry
 *
 * The Close (×) button hides the banner for the *current* session only —
 * the assumption itself stays active (use the header dropdown's "Stop
 * assuming role" action to end the session). A new assumption session
 * (different expiresAt) shows the banner again.
 *
 * Natural expiry is handled by the hook (it auto-clears + reloads).
 */

const WARNING_THRESHOLD_MS = 60_000
export const BANNER_HEIGHT_VAR = "--ct-banner-h"

// Dismissal is per-session, keyed by the current assumption's expiresAt
// (a fresh ISO string is minted on every /startRoleAssumption call), so
// starting a new assumption automatically re-shows the banner. Stored in
// sessionStorage so dismissal also survives in-tab navigation but resets
// when the tab closes.
const DISMISSED_STORAGE_KEY = "ct_role_assumption_banner_dismissed"

const readDismissedExpiresAt = () => {
  try {
    return sessionStorage.getItem(DISMISSED_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeDismissedExpiresAt = (expiresAt) => {
  try {
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, expiresAt)
  } catch {
    // sessionStorage unavailable (private mode, quota) — close still
    // hides the banner for this render via local state; it'll reappear
    // on next navigation, which is acceptable graceful degradation.
  }
}

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`
}

const RoleAssumptionBanner = () => {
  const { active, assumed, entry, remainingMs } = useRoleAssumption()
  const currentExpiresAt = entry?.expiresAt ?? null

  const [dismissed, setDismissed] = useState(() => !!currentExpiresAt && readDismissedExpiresAt() === currentExpiresAt)

  // Re-evaluate dismissal whenever the assumption session changes
  // (different expiresAt = different session = banner should reappear
  // unless that new session has also been dismissed).
  useEffect(() => {
    setDismissed(!!currentExpiresAt && readDismissedExpiresAt() === currentExpiresAt)
  }, [currentExpiresAt])

  // Publish the banner's height as --ct-banner-h so AppLayout's fixed side rail and the main
  // content area start below it (the banner is sticky at the top). Removed when the banner hides.
  const [bannerEl, setBannerEl] = useState(null)
  useEffect(() => {
    const root = document.documentElement
    if (!bannerEl) {
      root.style.removeProperty(BANNER_HEIGHT_VAR)
      return undefined
    }
    const apply = () => root.style.setProperty(BANNER_HEIGHT_VAR, `${Math.ceil(bannerEl.getBoundingClientRect().height)}px`)
    apply()
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null
    observer?.observe(bannerEl)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(BANNER_HEIGHT_VAR)
    }
  }, [bannerEl])

  if (!active || dismissed) return null

  const isWarning = remainingMs <= WARNING_THRESHOLD_MS

  const handleClose = () => {
    if (currentExpiresAt) writeDismissedExpiresAt(currentExpiresAt)
    setDismissed(true)
  }

  // Server-side role enum is lowercase ("sponsor"/"trainer"); capitalize
  // for display so the bold token next to "Acting as" reads naturally.
  const rawRole = assumed?.role
  const roleLabel = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1) : "—"
  const orgLabel = assumed?.orgName ?? assumed?.orgId ?? "organization"
  const countdown = formatCountdown(remainingMs)

  return (
    <Alert
      ref={setBannerEl}
      severity={isWarning ? "error" : "warning"}
      variant="filled"
      icon={<VisibilityIcon fontSize="inherit" />}
      // Default Alert role for warning/error severities is "alert"
      // (assertive), which interrupts screen readers. This banner is a
      // persistent context indicator, not a transient notification, so
      // demote to role="status" — it has an implicit aria-live="polite"
      // so the initial "Acting as X in Y" is announced once on mount.
      // No explicit aria-live on the Alert itself, otherwise the
      // per-second countdown updates inside would be re-announced
      // continuously while the banner is visible.
      role="status"
      action={
        <Stack direction="row" alignItems="center" spacing={1}>
          {/*
            aria-live="off" overrides the inherited polite live region
            from the role="status" parent — without this, screen readers
            announce the countdown every second, which is hostile during
            a 30-minute session. The static aria-label still lets users
            read the remaining time on demand by navigating to the
            element.
          */}
          <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }} aria-live="off" aria-label={`Session expires in ${countdown}`}>
            expires in {countdown}
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={handleClose}
            aria-label="Hide role assumption banner"
            title="Hide banner (assumption stays active — use the profile menu to stop)"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        borderRadius: 0,
        // MUI Alert's default padding stack (root + icon + message slots)
        // adds up to ~48px tall — appropriate for inline alerts, but too
        // chunky for a persistent page-top banner that costs viewport
        // height on every route. Tighten each slot to land around ~32-36px
        // while preserving the filled-severity theming and structure.
        py: 0.25,
        "& .MuiAlert-icon": { py: 0.5, mr: 1 },
        // Ellipsis the message on narrow viewports (long org names) so
        // the action area stays anchored on the right.
        "& .MuiAlert-message": {
          py: 0.5,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .MuiAlert-action": { py: 0, pl: 1, alignItems: "center" },
      }}
    >
      Acting as <strong>{roleLabel}</strong> in <strong>{orgLabel}</strong>
    </Alert>
  )
}

export default RoleAssumptionBanner
